import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Phone, MessageCircle } from 'lucide-react';
import Logo from './Logo';
import { BRAND_NAME } from '../utils/brand';
import { useReferralCta } from '../hooks/useReferralCta';
import { whatsappUrl } from '../utils/whatsapp';
import { trackWhatsAppClick, trackCtaClick } from '../utils/analytics';
import { PORTAL_CLINIC_SIGNIN_URL } from '../utils/ctaUrl';

const COLS = [
  // {
  //   label: 'Product',
  //   links: [
  //     { label: 'Weight Loss',  to: '/weight-loss' },
  //     { label: 'Plans',        to: '/packages' },
  //     { label: 'How it works', to: '/how-it-works' },
  //     { label: 'Features',     to: '/features' },
  //     { label: `Why ${BRAND_NAME}`, to: '/why-us' }
  //   ]
  // },
  // {
  //   label: 'Company',
  //   links: [
  //     { label: 'About',              to: '/about' },
  //     { label: 'Contact',            to: '/contact' },
  //     { label: 'FAQ',                to: '/faq' },
  //     { label: 'Preventive Health',  to: '/preventive-health' },
  //     { label: 'For clinics',        href: 'mailto:clinics@mydoctorai.local' }
  //   ]
  // },
  {
    label: 'Legal',
    links: [
      // { label: 'Safety Information',          to: '/safety' },
      // { label: 'Sensitive Data Consent',      to: '/sensitive-data-consent' },
      { label: 'Privacy Policy',              to: '/privacy' },
      { label: 'Terms & Conditions',          to: '/terms' },
      { label: 'Medical disclaimer',          to: '/terms#medical-disclaimer' }
    ]
  }
];

const linkCls = 'text-sm text-ink-300 hover:text-white transition-colors';
const headCls = 'text-xs uppercase tracking-[0.18em] text-ink-500 mb-4';

export default function Footer() {
  const { signInUrl } = useReferralCta();
  return (
    <footer className="bg-ink-900 text-ink-300">
      <div className="container-x py-14 sm:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-x-8 gap-y-10">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-5 lg:pr-8">
            <Logo className="text-white" />
            <p className="mt-5 text-sm text-ink-400 max-w-md leading-relaxed">
              {BRAND_NAME} is a healthcare technology and care coordination platform. We help
              eligible adults access doctor-guided weight management programmes through an
              AI-assisted assessment, secure booking and partner clinic coordination.
            </p>
            <p className="mt-3 text-sm text-ink-400 max-w-md leading-relaxed">
              Consultation, diagnosis and treatment decisions are made by independent licensed
              healthcare providers. Programme suitability is subject to doctor consultation.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/intake"
                className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-5 py-2.5 bg-white text-ink-900 hover:bg-ink-100 transition-colors"
              >
                Am I Qualified? <ArrowRight size={14} />
              </Link>
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick({ source_page: 'footer', cta_label: 'WhatsApp' })}
                className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-5 py-2.5 bg-[#25D366] text-white hover:bg-[#1ebe5b] transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
          </div>

          {/* Link columns */}
          {COLS.map((col) => (
            <div key={col.label} className="lg:col-span-2">
              <div className={headCls}>{col.label}</div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.to
                      ? <Link className={linkCls} to={l.to}>{l.label}</Link>
                      : <a className={linkCls} href={l.href}>{l.label}</a>}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <div className={headCls}>Account</div>
            <ul className="space-y-3">
              <li><a className={linkCls} href={signInUrl}>Patient sign in</a></li>
              <li>
                <a
                  className={linkCls}
                  href={PORTAL_CLINIC_SIGNIN_URL}
                  onClick={() => trackCtaClick({ page: 'footer', button_label: 'Clinic sign in', destination: 'portal-login' })}
                >
                  Clinic sign in
                </a>
              </li>
              <li><Link className={linkCls} to="/contact">Contact us</Link></li>
            </ul>
          </div>

          <div className="col-span-2 sm:col-span-1 lg:col-span-3">
            <div className={headCls}>Get in touch</div>
            <ul className="space-y-3 text-sm text-ink-300">
              <li className="flex items-center gap-2.5">
                <MapPin size={15} className="text-ink-500 shrink-0" /> Kuala Lumpur, Malaysia
              </li>
              <li>
                <a className={`${linkCls} inline-flex items-center gap-2.5`} href="mailto:hello@mydrai.co">
                  <Mail size={15} className="text-ink-500 shrink-0" /> hello@mydrai.co
                </a>
              </li>
              <li>
                <a className={`${linkCls} inline-flex items-center gap-2.5`} href="tel:+601120801876">
                  <Phone size={15} className="text-ink-500 shrink-0" /> +60 11-2080 1876
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Stay updated — routes to the Early Access form on /preventive-health */}
        <div className="mt-12 rounded-2xl border border-ink-800 bg-white/[0.03] px-5 py-5 sm:px-8 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">Stay updated</div>
            <p className="mt-1 text-sm text-ink-400">
              Be first to hear about preventive health screening, new lab partners and early-access offers. No spam.
            </p>
          </div>
          <Link
            to="/preventive-health"
            onClick={() => trackCtaClick({ page: 'footer', button_label: 'Join Early Access', destination: '/preventive-health' })}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-600 text-white text-sm font-semibold px-6 py-3 hover:bg-brand-700 transition-colors"
          >
            Join Early Access <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-10 pt-6 border-t border-ink-800 grid sm:grid-cols-2 gap-3 text-xs text-ink-500">
          <span>&copy; {new Date().getFullYear()} Melonco Sdn. Bhd. All rights reserved.</span>
          <span className="sm:text-right">Doctor-led · AI-assisted · Personalised for Malaysians</span>
        </div>

        <div className="mt-6 space-y-4 max-w-4xl">
          <p className="text-[11px] text-ink-500 leading-relaxed">
            <strong className="text-ink-300">Medical Disclaimer.</strong> {BRAND_NAME} is a
            healthcare technology and care coordination platform operated by Melonco
            Sdn. Bhd. {BRAND_NAME} helps users access doctor-guided weight management
            programmes through independent licensed healthcare providers and partner
            clinics. {BRAND_NAME} does not itself practise medicine, diagnose medical
            conditions, prescribe medication, dispense medication, or replace the
            judgment of qualified healthcare professionals. Assessments, consultations
            and treatment decisions are made solely by independent licensed healthcare
            providers. The AI-assisted assessment and platform content support intake,
            education and care coordination only and do not by themselves create a
            doctor–patient relationship. Programme suitability is subject to doctor
            consultation. Individual experiences may vary.
          </p>
          <p className="text-[11px] text-ink-500 leading-relaxed">
            <strong className="text-ink-300">Emergency notice.</strong> {BRAND_NAME} does
            not provide emergency medical services. If you have a medical emergency or
            serious symptoms, seek immediate medical attention.
          </p>
          <p className="text-[11px] text-ink-500 leading-relaxed">
            <strong className="text-ink-300">AI content disclosure.</strong> Some
            visuals and interface mockups may be generated or enhanced using
            artificial intelligence. Platform visuals are illustrative and should not
            be interpreted as guaranteed outcomes.
          </p>
        </div>
      </div>
    </footer>
  );
}
